"use client";

/**
 * Full-screen photo viewer for the result page - no deps, one file.
 *
 * The page behind stays visible but grayed out (constant translucent
 * backdrop). Photos swap instantly - no slide/carousel animation.
 * Desktop: prev/next arrows + arrow keys. Mobile: horizontal swipe
 * (touch delta, still an instant swap). ESC / backdrop-click closes.
 * The current photo's room label sits bottom-center.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Waves, Trees } from "lucide-react";
import { useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import { haptic } from "@/lib/haptics";
import { TIER_STYLE } from "@/components/photo-tiers";
import { useBackToClose } from "@/lib/use-back-to-close";
import { captureEvent } from "@/lib/tracking";
import { withRec, type RecRef } from "@/lib/rec-client";

export interface LightboxPhoto {
  url: string;
  /** Scene caption ("Yataq otağı 2"); null/absent = no caption. */
  label?: string | null;
  /** Condition tier of THIS photo ("luxury".."shell") - rendered as a
   *  colored badge next to the label. */
  tier?: string | null;
  /** Asserted view in THIS photo ("sea" | "greenery"). */
  view?: string | null;
}

// Stored galleries can carry small CDN variants; the viewer always
// wants the full-size original.
const fullSize = (u: string) =>
  u.replace(/\/uploads\/(large|medium|small)\//, "/uploads/full/");

export default function PhotoLightbox({
  photos,
  initialIndex,
  onClose,
  track,
}: {
  photos: LightboxPhoto[];
  initialIndex: number;
  onClose: () => void;
  /** A listing's gallery: `listing.gallery.open` {id, n, of} goes when
   *  it closes (or the page goes away), `n` the photos seen. */
  track?: { id: string; rec?: RecRef | null } | null;
}) {
  const t = useT();
  const [cur, setCur] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(photos.length - 1, 0)),
  );
  // The photos seen, for the gallery event.
  const seen = useRef<Set<number>>(new Set());
  useEffect(() => { seen.current.add(cur); }, [cur]);
  const trackRef = useRef(track);
  useEffect(() => { trackRef.current = track; }, [track]);
  const total = photos.length;
  useEffect(() => {
    let sent = false;
    const openedAt = Date.now();
    const send = (e?: Event) => {
      const tr = trackRef.current;
      // Under 150 ms is Strict Mode's mount-unmount in development.
      if (sent || !tr?.id || Date.now() - openedAt < 150) return;
      sent = true;
      captureEvent("listing.gallery.open", withRec({ id: tr.id, n: seen.current.size, of: total }, tr.rec), { beacon: e?.type === "pagehide" });
    };
    window.addEventListener("pagehide", send);
    return () => {
      window.removeEventListener("pagehide", send);
      send();
    };
    // One event per opening: the effect lives as long as the viewer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const touchX = useRef<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Body scroll lock while open + move focus INTO the overlay: the
  // opener (a strip thumbnail inside a horizontal scroll container)
  // keeps focus otherwise, and arrow keys then scroll the strip and
  // the page BEHIND the lightbox.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const opener = document.activeElement as HTMLElement | null;
    rootRef.current?.focus();
    return () => {
      document.body.style.overflow = prev;
      opener?.focus?.();
    };
  }, []);

  // Wraps around: the session replays showed the disabled "next"
  // arrow on the last photo collecting rage clicks (people expect a
  // gallery to loop). With a single photo it stays put.
  const step = (dir: -1 | 1) =>
    setCur((c) => {
      if (photos.length < 2) return c;
      const next = (c + dir + photos.length) % photos.length;
      haptic(4);
      return next;
    });

  // Hardware Back closes the lightbox, not the page (owner, 2026-10-10).
  useBackToClose(true, onClose);
  // Keyboard: ESC close, arrows navigate. preventDefault so the
  // browser's default arrow-key scrolling can't shift any scrollable
  // content behind the overlay (incl. at the ends, where the index
  // no longer changes but the default would still fire).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        step(1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        step(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, photos.length]);

  if (photos.length === 0) return null;
  const photo = photos[cur];

  return createPortal(
    <div
      ref={rootRef}
      tabIndex={-1}
      // Opaque, not /85: the translucent backdrop let the page show
      // through the letterbox, and stepping between photos of
      // different aspect ratios shifted which slice of the page was
      // visible - users read it as "the background images change".
      className="fixed inset-0 z-[1300] bg-black outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={t("photointel.title")}
      onClick={onClose}
      onTouchStart={(e) => {
        touchX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        touchX.current = null;
        const end = e.changedTouches[0]?.clientX;
        if (start == null || end == null) return;
        const dx = end - start;
        if (Math.abs(dx) > 48) step(dx < 0 ? 1 : -1);
      }}
    >
      {/* Header: counter left, close right. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-3 bg-gradient-to-b from-black/70 to-transparent px-4 pt-4 pb-8 sm:px-6">
        <p className="text-sm font-bold tabular-nums text-white">
          {cur + 1} / {photos.length}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("onboard.dismiss")}
          className="pointer-events-auto shrink-0 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Current photo - swaps instantly, no carousel. */}
      <div className="flex h-full w-full items-center justify-center p-3 pt-16 pb-14 sm:p-12">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          key={photo.url}
          src={fullSize(photo.url)}
          alt={photo.label ?? ""}
          decoding="async"
          draggable={false}
          // The <img> element spans the whole stage (h/w-full) while
          // object-contain paints a smaller rect - so a click in the
          // black letterbox still hit the img and its blanket
          // stopPropagation ate the backdrop-close. Only swallow
          // clicks that land on the PAINTED image; letterbox clicks
          // bubble to the root and close, as users expect.
          onClick={(e) => {
            const el = e.currentTarget;
            const rect = el.getBoundingClientRect();
            if (!el.naturalWidth || !el.naturalHeight) {
              e.stopPropagation();
              return;
            }
            const scale = Math.min(
              rect.width / el.naturalWidth,
              rect.height / el.naturalHeight,
            );
            const w = el.naturalWidth * scale;
            const h = el.naturalHeight * scale;
            const x0 = rect.left + (rect.width - w) / 2;
            const y0 = rect.top + (rect.height - h) / 2;
            const onImage =
              e.clientX >= x0 && e.clientX <= x0 + w
              && e.clientY >= y0 && e.clientY <= y0 + h;
            if (onImage) e.stopPropagation();
          }}
          // h/w-full + object-contain letterboxes AND upscales: some
          // stored galleries carry small CDN variants that would
          // otherwise float as a tiny postage stamp.
          className="h-full w-full rounded-lg object-contain select-none"
        />
      </div>

      {/* Preload neighbors so stepping never shows a blank frame. */}
      {[cur - 1, cur + 1]
        .filter((i) => i >= 0 && i < photos.length)
        .map((i) => (
          // eslint-disable-next-line @next/next/no-img-element
          <img key={photos[i].url} src={fullSize(photos[i].url)} alt="" className="hidden" />
        ))}

      {/* Scene label + per-photo badges - which room is this, its
          renovation tier (colored, meter palette) and any asserted
          view. Bottom-center, always readable over any image. */}
      {(photo.label || photo.tier || photo.view) && (
        <div className="pointer-events-none absolute inset-x-0 bottom-6 z-10 flex flex-wrap items-center justify-center gap-2 px-4">
          {photo.label && (
            <span className="max-w-full truncate rounded-full bg-black/65 px-4 py-1.5 text-sm font-semibold text-white">
              {photo.label}
            </span>
          )}
          {photo.tier && TIER_STYLE[photo.tier] && (
            <span className={`rounded-full border px-3 py-1.5 text-sm font-bold shadow ${TIER_STYLE[photo.tier].chip}`}>
              {t(`photointel.tier.${photo.tier}` as TranslationKey)}
            </span>
          )}
          {(photo.view === "sea" || photo.view === "greenery") && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-bold shadow ${
                photo.view === "sea"
                  ? "bg-gray-100 text-gray-700 border-gray-200"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
              }`}
            >
              {photo.view === "sea"
                ? <Waves className="h-4 w-4" />
                : <Trees className="h-4 w-4" />}
              {t(`photointel.seen.${photo.view}` as TranslationKey)}
            </span>
          )}
        </div>
      )}

      {photos.length > 1 && (
        <>
          {/* Phone: the outer quarter on each side is a tap zone (a swipe
              still works). Replays showed people tapping the edges where
              desktop arrows would be and getting nothing. Transparent,
              below the header so the close button stays reachable. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            aria-label={t("listings.detail.prev_photo")}
            className="absolute inset-y-16 left-0 z-[5] w-1/4 sm:hidden no-scale"
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            aria-label={t("listings.detail.next_photo")}
            className="absolute inset-y-16 right-0 z-[5] w-1/4 sm:hidden no-scale"
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            aria-label={t("listings.detail.prev_photo")}
            className="absolute left-3 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 transition-colors sm:flex"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            aria-label={t("listings.detail.next_photo")}
            className="absolute right-3 top-1/2 z-10 hidden -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20 transition-colors sm:flex"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}
    </div>
  , document.body);
}
